import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Heart, ArrowRight } from 'lucide-react';
import AnnouncementBanner from './AnnouncementBanner';
import FloatingDonateTab from './FloatingDonateTab';
import FloatingLanguageTab from './FloatingLanguageTab';
import LanguageSelector from './LanguageSelector';
import TranslatedPage from './TranslatedPage';
import TranslationStatusPill from './TranslationStatusPill';
import { useT } from '../contexts/LanguageContext';

const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_humanitarian-aid-7/artifacts/5fygylhw_IMG_0281.jpeg";

// Condensed 5-group nav. Every original URL is still reachable — the
// only change is grouping. Single-item groups (Events) render as a
// plain link. Multi-item groups render as a hover/click dropdown on
// desktop and a collapsible section on mobile. `/international`
// (Global Programs) lives under the Programs dropdown so no route is
// lost from the nav.
const NAV_GROUPS = [
  {
    id: 'about', label: 'About Us',
    // Landing route when the parent label itself is clicked. Keeps the
    // dropdown parent behaving like a link too.
    path: '/about',
    items: [
      { name: 'About Us', path: '/about' },
      { name: 'Our Impact', path: '/impact' },
    ],
  },
  {
    id: 'programs', label: 'Programs', path: '/projects',
    items: [
      { name: 'Programs', path: '/projects' },
      { name: 'Global Programs', path: '/international' },
      { name: 'Campaigns', path: '/campaigns' },
    ],
  },
  {
    id: 'events', label: 'Events', path: '/events',
    // no `items` → renders as a single link, no dropdown
  },
  {
    id: 'news', label: 'News & Media', path: '/updates',
    items: [
      { name: 'Updates', path: '/updates' },
      { name: 'Gallery', path: '/gallery' },
    ],
  },
  {
    id: 'involved', label: 'Get Involved', path: '/involved',
    items: [
      { name: 'Get Involved', path: '/involved' },
      { name: 'Shop', path: '/shop' },
      { name: 'Contact Us', path: '/contact' },
    ],
  },
];

// Flat list of every reachable route — used by the footer so no page
// disappears from the site map even though the header is condensed.
const FOOTER_LINKS = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Programs', path: '/projects' },
  { name: 'Global Programs', path: '/international' },
  { name: 'Campaigns', path: '/campaigns' },
  { name: 'Events', path: '/events' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Our Impact', path: '/impact' },
  { name: 'Updates', path: '/updates' },
  { name: 'Shop', path: '/shop' },
  { name: 'Get Involved', path: '/involved' },
  { name: 'Contact Us', path: '/contact' },
];

// --- Desktop dropdown group ------------------------------------------
const NavGroup = ({ group, isActivePath, t }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const single = !group.items || group.items.length === 0;

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (!single) setOpen(true);
  };
  const handleLeave = () => {
    // Small delay so users can move diagonally into the dropdown.
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const isActive = single
    ? isActivePath(group.path)
    : (group.items || []).some((i) => isActivePath(i.path));

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}
         data-testid={`nav-group-${group.id}`}>
      <Link
        to={group.path}
        className={`nav-link text-sm whitespace-nowrap font-medium inline-flex items-center gap-1 ${isActive ? 'active' : ''}`}
        data-testid={`nav-${group.id}`}
        aria-haspopup={single ? undefined : 'true'}
        aria-expanded={single ? undefined : open}
      >
        {t(group.label)}
        {!single && <ChevronDown className="w-3.5 h-3.5" />}
      </Link>
      {!single && open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[180px] bg-white border border-gray-200 rounded-md shadow-lg py-1.5 z-50"
             data-testid={`nav-dropdown-${group.id}`}>
          {group.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`block px-4 py-2 text-sm whitespace-nowrap hover:bg-[#F9FAFB] transition-colors ${
                isActivePath(item.path)
                  ? 'text-[#BA1826] font-semibold'
                  : 'text-[#0A0A0A]'
              }`}
              data-testid={`nav-item-${item.path.replace(/\//g, '-')}`}
              onClick={() => setOpen(false)}
            >
              {t(item.name)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Mobile collapsible group ---------------------------------------
const MobileNavGroup = ({ group, isActivePath, t, onNavigate }) => {
  const single = !group.items || group.items.length === 0;
  const groupActive = single
    ? isActivePath(group.path)
    : (group.items || []).some((i) => isActivePath(i.path));
  const [open, setOpen] = useState(groupActive);

  if (single) {
    return (
      <Link
        to={group.path}
        onClick={onNavigate}
        className={`block text-lg font-medium py-3 border-b border-gray-100 ${
          groupActive ? 'text-[#BA1826]' : 'text-gray-800'
        }`}
        data-testid={`mobile-nav-${group.id}`}
      >
        {t(group.label)}
      </Link>
    );
  }
  return (
    <div className="border-b border-gray-100" data-testid={`mobile-nav-group-${group.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-lg font-medium py-3 text-left"
        data-testid={`mobile-nav-${group.id}-toggle`}
        aria-expanded={open}
      >
        <span className={groupActive ? 'text-[#BA1826]' : 'text-gray-800'}>
          {t(group.label)}
        </span>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-2 pl-3" data-testid={`mobile-nav-${group.id}-items`}>
          {group.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={`block py-2 text-base ${
                isActivePath(item.path)
                  ? 'text-[#BA1826] font-semibold'
                  : 'text-gray-700'
              }`}
              data-testid={`mobile-nav-item-${item.path.replace(/\//g, '-')}`}
            >
              {t(item.name)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const t = useT();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;
  const closeMobile = () => { setIsMobileMenuOpen(false); window.scrollTo(0, 0); };

  return (
    <div className="min-h-screen flex flex-col heart-pattern-bg">
      {/* Floating Donate Tab */}
      <FloatingDonateTab />
      <FloatingLanguageTab />
      <TranslationStatusPill />
      
      {/* Header */}
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
          isScrolled ? 'lg:glass-header shadow-sm' : ''
        }`}
      >
        {/* Site-wide Announcement Banner (top of every page) */}
        <AnnouncementBanner />
        {/* Brand row — wordmark centered, hamburger absolute-right on
            mobile. Padding tightened so more content sits above the
            fold. Height stays visually consistent across pages via the
            shared .nhf-wordmark class. */}
        <div className="border-b border-gray-100">
          <div className="max-w-[900px] mx-auto px-4 relative flex items-center justify-center py-2.5 md:py-3">
            <Link
              to="/"
              className="flex items-center"
              data-testid="logo-link"
              aria-label="Novena Humanitarian Foundation home"
            >
              <span className="nhf-wordmark text-[13px] xs:text-sm sm:text-base md:text-lg lg:text-xl"
                    data-testid="nhf-wordmark">
                Novena Humanitarian Foundation
              </span>
            </Link>
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 absolute right-4 top-1/2 -translate-y-1/2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Bottom row: Desktop nav (grouped dropdowns) */}
        <div className="hidden lg:block border-b border-gray-50 bg-white">
          <nav className="max-w-[900px] mx-auto px-4 flex items-center justify-between gap-4 py-3"
               data-testid="desktop-nav">
            {/* Left spacer keeps the nav-group cluster visually centered */}
            <div className="flex-1" />
            <div className="flex items-center gap-5">
              {NAV_GROUPS.map((g) => (
                <NavGroup key={g.id} group={g} isActivePath={isActive} t={t} />
              ))}
              <Link
                to="/donate"
                className="btn-primary text-xs px-4 py-2 uppercase tracking-wide"
                data-testid="header-donate-btn"
              >
                {t('Donate Now')}
              </Link>
            </div>
            <div className="flex-1 flex justify-end">
              <LanguageSelector />
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu — collapsible sections + Donate CTA */}
      <div
        className={`lg:hidden fixed inset-0 top-16 bg-white transform transition-transform duration-300 z-[60] shadow-2xl overflow-y-auto ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: '#ffffff' }}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 bg-white min-h-full">
          <Link
            to="/"
            onClick={closeMobile}
            className={`block text-lg font-medium py-3 border-b border-gray-100 ${
              isActive('/') ? 'text-[#BA1826]' : 'text-gray-800'
            }`}
            data-testid="mobile-nav-home"
          >
            {t('Home')}
          </Link>
          {NAV_GROUPS.map((g) => (
            <MobileNavGroup key={g.id} group={g} isActivePath={isActive}
                            t={t} onNavigate={closeMobile} />
          ))}
          <Link
            to="/donate"
            onClick={closeMobile}
            className="btn-primary text-center mt-6 py-3 uppercase tracking-wide"
            data-testid="mobile-donate-btn"
          >
            {t('Donate Now')}
          </Link>
          <div className="mt-4">
            <LanguageSelector variant="mobile" />
          </div>
        </nav>
      </div>

      {/* Main Content - Centered white container */}
      <main className="flex-1 pt-24 lg:pt-[120px]">
        <div className="max-w-[900px] mx-auto bg-white border-x-[3px] border-black min-h-screen shadow-lg">
          <TranslatedPage context="public_page">
            {children}
          </TranslatedPage>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer-dark py-12" data-testid="main-footer">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src={LOGO_IMAGE} 
                  alt="Novena Humanitarian Foundation Logo" 
                  className="w-10 h-10 object-contain rounded"
                />
                <span className="font-heading font-bold text-xl text-white">
                  Novena Humanitarian Foundation
                </span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Transparency without exploitation. Support without shame.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {FOOTER_LINKS.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="footer-link text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/donate" className="footer-link text-sm">Make a Donation</Link>
                </li>
                <li>
                  <Link to="/involved" className="footer-link text-sm">Volunteer</Link>
                </li>
                <li>
                  <Link to="/involved" className="footer-link text-sm">Partner With Us</Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="mailto:novenahfoundation@gmail.com" className="footer-link">
                    novenahfoundation@gmail.com
                  </a>
                </li>
                <li>
                  <a href="tel:347-596-5776" className="footer-link">
                    347-596-5776
                  </a>
                </li>
                <li className="text-gray-500">
                  New York, NY
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 mb-6 text-center" data-testid="footer-501c3-notice">
              <p className="text-white text-xs font-semibold leading-relaxed mb-1">
                IRS Recognized 501(c)(3) Public Charity
              </p>
              <p className="text-gray-400 text-xs leading-relaxed">
                Novena Humanitarian Foundation, Inc. is officially recognized by the Internal Revenue Service (IRS) as a 501(c)(3) public charity.<br />
                Employer Identification Number (EIN): 41-4097072<br />
                Effective Date of Federal Tax-Exempt Status: February 2, 2026<br />
                Contributions are tax-deductible to the extent permitted by law.
              </p>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} Novena Humanitarian Foundation. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/admin/login" className="text-gray-600 hover:text-gray-400 text-xs transition-colors" data-testid="footer-admin-link">
                  Admin
                </Link>
                <p className="text-gray-600 text-xs">
                  Helping without compromising dignity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
